import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Flame, Copy, Check } from 'lucide-react';
import { Button } from './Button';
const COUPON_CODE = 'CODIGOSOMBRA';
const CHECKOUT_URL = `https://pay.hotmart.com/G105750225G?off=exnypaed&coupon=${COUPON_CODE}`;
export function ExitIntentPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasTriggeredRef = useRef(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = COUPON_CODE;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
      document.body.removeChild(textArea);
    }
  };
  // Trigger detection
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const trigger = () => {
      if (hasTriggeredRef.current) return;
      hasTriggeredRef.current = true;
      setIsOpen(true);
    };
    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY > 0) return;
      const related = (e.relatedTarget || (e as any).toElement) as Node | null;
      if (related) return;
      trigger();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        setTimeout(() => {
          if (document.visibilityState === 'visible') trigger();
        }, 200);
      }
    };
    let lastScrollY = window.scrollY;
    let lastTime = Date.now();
    const handleScroll = () => {
      if (hasTriggeredRef.current) return;
      const now = Date.now();
      const dy = lastScrollY - window.scrollY;
      const dt = now - lastTime;
      if (dy > 60 && dt < 250 && window.scrollY < 300) {
        trigger();
      }
      lastScrollY = window.scrollY;
      lastTime = now;
    };
    // Smarter idle fallback: only fire if user hasn't reached the offer/bottom of page
    const idleTimer = window.setTimeout(() => {
      const docHeight = document.documentElement.scrollHeight;
      const scrolledFraction = (window.scrollY + window.innerHeight) / docHeight;
      // Skip if user is already in the last 25% of the page (they've seen the offer)
      if (scrolledFraction < 0.75) {
        trigger();
      }
    }, 75000);
    const attachTimer = window.setTimeout(() => {
      document.addEventListener('mouseout', handleMouseOut);
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('scroll', handleScroll, {
        passive: true
      });
    }, 2000);
    return () => {
      window.clearTimeout(attachTimer);
      window.clearTimeout(idleTimer);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Body scroll lock + ESC key + custom events to coordinate with FloatingCTA
  useEffect(() => {
    if (isOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      window.dispatchEvent(new CustomEvent('exit-popup-open'));
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsOpen(false);
      };
      document.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = previousOverflow;
        document.removeEventListener('keydown', handleEsc);
        window.dispatchEvent(new CustomEvent('exit-popup-close'));
      };
    }
  }, [isOpen]);
  const close = () => setIsOpen(false);
  return <AnimatePresence data-id="element-19">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6" onClick={close} role="dialog" aria-modal="true" aria-labelledby="exit-popup-title" data-id="element-20">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" data-id="element-21"></div>

          <motion.div initial={{
        opacity: 0,
        scale: 0.92,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.92,
        y: 20
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative max-w-lg w-full max-h-[90vh] overflow-y-auto bg-darker border border-blood/40 shadow-[0_0_60px_rgba(184,20,28,0.25)]" onClick={e => e.stopPropagation()} data-id="element-22">
            <div className="absolute top-0 left-0 w-full h-1 bg-blood" data-id="element-23"></div>

            <button onClick={close} className="absolute top-4 right-4 text-foreground/50 hover:text-blood transition-colors p-1 z-10" aria-label="Cerrar" data-id="element-24">
              <X size={22} data-id="element-25" />
            </button>

            <div className="p-8 md:p-10 text-center" data-id="element-26">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blood/15 text-blood mb-6" data-id="element-27">
                <Flame size={26} strokeWidth={1.8} data-id="element-28" />
              </div>

              <span className="text-blood font-sans text-xs font-bold tracking-widest uppercase mb-4 block" data-id="element-29">
                Espera — antes de irte
              </span>

              <h3 id="exit-popup-title" className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4 leading-tight" data-id="element-30">
                Una última oferta para los que se atreven.
              </h3>

              <p className="text-foreground/70 font-sans font-light leading-relaxed mb-8" data-id="element-31">
                Sé que el conocimiento prohibido tiene un precio. Por eso, antes
                de cerrar esta página, te ofrezco acceso completo a{' '}
                <strong className="text-foreground font-normal" data-id="element-32">
                  El Código de la Sombra
                </strong>{' '}
                — junto con todos los bonos — al precio más bajo que jamás
                verás.
              </p>

              <div className="border border-border bg-background p-6 mb-8" data-id="element-33">
                <span className="text-foreground/50 font-sans text-xs uppercase tracking-widest block mb-3" data-id="element-34">
                  Oferta exclusiva de salida
                </span>
                <div className="flex items-baseline justify-center gap-3 flex-wrap mb-2" data-id="element-35">
                  <span className="text-xl md:text-2xl font-serif text-foreground/40 line-through decoration-blood/70 decoration-2" data-id="element-36">
                    $27
                  </span>
                  <span className="text-lg md:text-xl font-serif text-foreground/40 line-through decoration-blood/70 decoration-2" data-id="element-37">
                    $14.99
                  </span>
                  <span className="text-4xl md:text-5xl font-serif font-bold text-blood" data-id="element-38">
                    $12.99
                  </span>
                  <span className="text-base font-sans text-foreground/60" data-id="element-39">
                    USD
                  </span>
                </div>
                <p className="text-foreground/60 font-sans text-xs uppercase tracking-widest" data-id="element-40">
                  Pago único · Acceso de por vida
                </p>
              </div>

              <div className="mb-6" data-id="element-41">
                <p className="text-foreground/50 font-sans text-xs uppercase tracking-widest mb-3" data-id="element-42">
                  Usa este código en el checkout
                </p>
                <div className="relative border-2 border-dashed border-blood/50 bg-blood/5 p-4 flex items-center justify-between gap-3" data-id="element-43">
                  <div className="flex-1 text-left min-w-0" data-id="element-44">
                    <span className="block text-[10px] font-sans uppercase tracking-widest text-foreground/40 mb-1" data-id="element-45">
                      Cupón de descuento
                    </span>
                    <span className="font-mono text-lg md:text-2xl font-bold text-foreground tracking-wider truncate block" data-id="element-46">
                      {COUPON_CODE}
                    </span>
                  </div>
                  <button onClick={handleCopy} className={`flex-shrink-0 inline-flex items-center gap-2 px-3 md:px-4 py-2 border font-sans text-xs font-bold uppercase tracking-widest transition-all ${copied ? 'bg-blood text-white border-blood' : 'border-blood text-blood hover:bg-blood hover:text-white'}`} aria-label="Copiar código de cupón" data-id="element-47">
                    {copied ? <>
                        <Check size={14} strokeWidth={2.5} data-id="element-48" />
                        Copiado
                      </> : <>
                        <Copy size={14} strokeWidth={2.5} data-id="element-49" />
                        Copiar
                      </>}
                  </button>
                </div>
                <p className="text-foreground/50 font-sans text-[11px] mt-2" data-id="element-50">
                  Se aplica automáticamente al hacer clic abajo. Válido por
                  tiempo limitado.
                </p>
              </div>

              <Button fullWidth href={CHECKOUT_URL} onClick={close} data-id="element-51">
                Aplicar cupón y completar compra
              </Button>

              <button onClick={close} className="mt-5 text-foreground/40 hover:text-foreground/70 transition-colors font-sans text-xs uppercase tracking-widest" data-id="element-52">
                No, prefiero pagar el precio completo después
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}